/*
 * What a customer order is in, and what the customer can do with it - for
 * every theme and every page that shows orders (Orders list, order manage
 * dialog). Only raw Prodyflow fields are read; the windows (return / late
 * return) are decided by the backend (allow_return, allow_dispute from the
 * sale channel's return_allowed_days / dispute_allowed_days).
 *
 * Enums (core-backend): OrderStatusesEnum (-1 not finished, 0 waiting,
 * 1 under process, 2 successful, 3 failed), PaymentStatusesEnum (2 payed,
 * 3 partially payed, 4 refund, 5 partial refund, 6 wait for refund, 7 failed,
 * 8 need manual refund), DeliveryStatusesEnum (1 packaging, 2 wait for
 * delivery, 3 under delivery, 5 delivered, 6 finished, 7 cancelled,
 * 8 wait for pickup).
 */
const OrderRules = {
    // after-sale flags: on the order itself in the customer list, in after_status elsewhere
    afterStatus(order) {
        const src = (order && order.after_status) || order || {};
        return {
            is_change: !!src.is_change,
            is_refund: !!src.is_refund,
            is_deny: !!src.is_deny,
            is_refused: !!src.is_refused,
        };
    },
    isDelivered(order) {
        return [5, 6].includes(Number(order.delivered_status));
    },
    // status 3 alone is not a payment failure: the backend also sets it when an order is cancelled (denyOrder)
    isPaymentFailed(order) {
        return Number(order.payment_status) === 7;
    },
    // partly paid (the database sets payment_status 3 when less is paid than the order is worth) and not a cash on delivery
    // order, which is settled at the delivery
    owesPayment(order) {
        if (Number(order.payment_status) !== 3) return false;
        return !(order.orderPayments || []).some((p) => p && p.PaymentMethod && Number(p.PaymentMethod.type) === 2);
    },
    isPaid(order) {
        return [2, 3].includes(Number(order.payment_status));
    },

    /*
     * {kind, stopped, pay, returning, step}
     * kind: refused | denied | failed | cancelled | unfinished | refund | changed | delivered | pickup | shipping | packing | processing
     * step (outbound): 1 ordered, 2 packing, 3 on the way, 4 delivered
     */
    state(order) {
        const after = this.afterStatus(order);
        const ds = Number(order.delivered_status), ps = Number(order.payment_status), st = Number(order.status);
        if (after.is_refused) return { kind: 'refused', stopped: true, pay: false, returning: false, step: 0 };
        if (after.is_deny || ds === 7 || st === 3) {
            // denied: the customer (or the shop) cancelled it; failed: the card payment did not go through
            const kind = after.is_deny ? 'denied' : (this.isPaymentFailed(order) ? 'failed' : 'cancelled');
            return { kind, stopped: true, pay: false, returning: false, step: 0 };
        }
        if (st === -1) return { kind: 'unfinished', stopped: true, pay: true, returning: false, step: 0 };
        // the order was paid, then its value grew (a product added in the admin, an exchange): the difference is to be
        // paid on the payment page (it pays what is left)
        const owe = this.owesPayment(order);

        let step = 1;
        if (this.isDelivered(order)) step = 4;
        else if (ds === 3 || ds === 8 || Number(order.order_items_sum_sent_out_quantity) > 0) step = 3;
        else if (ds === 1 || ds === 2 || Number(order.order_items_sum_collected_quantity) > 0) step = 2;

        // a refund in motion or done: a return was made (is_refund), the money is due back (6) or its refund failed (8), or
        // everything came back (4). 5 (partial refund) alone is settled - the order is paid up after a refund, e.g. the
        // price difference of an exchange - so it is no refund in progress: the order can still be returned / exchanged
        if (after.is_refund || [4, 6, 8].includes(ps)) return { kind: 'refund', stopped: false, pay: owe, returning: true, step: Math.max(step, 4) };
        if (after.is_change) return { kind: 'changed', stopped: false, pay: owe, returning: false, step };
        if (step === 4) return { kind: 'delivered', stopped: false, pay: owe, returning: false, step };
        if (ds === 8) return { kind: 'pickup', stopped: false, pay: owe, returning: false, step };
        if (step === 3) return { kind: 'shipping', stopped: false, pay: owe, returning: false, step };
        return { kind: step === 2 ? 'packing' : 'processing', stopped: false, pay: owe, returning: false, step };
    },

    // not accepted yet: address, size and cancel are still possible. A pre-ordered order
    // (status -2: the list's computed value for an unaccepted order with items not in stock,
    // OrderManager::calculatwRows; in the database it is still 0) is waiting like any other
    canModify(order) {
        return !!order && (order.status === null || [0, -2].includes(Number(order.status))) && order.accepted == null
            && Number(order.order_items_sum_quantity) > 0 && Number(order.delivered_status) !== 7;
    },
    isPreorder(order) {
        return !!order && Number(order.status) === -2;
    },
    // 'open' = return, 'late' = late return (with a reason), '' = none
    returnWindow(order) {
        if (!order) return '';
        if (order.allow_return) return 'open';
        return order.allow_dispute ? 'late' : '';
    },
    // shipped at least a day ago (or delivered), and the channel allows it
    canReturn(order, channel) {
        if (!order || this.state(order).stopped) return false;
        if (!(Number(order.status) > 0 && order.accepted && Number(order.order_items_sum_quantity) > 0)) return false;
        const delivery = (order.orderDeliveries && order.orderDeliveries[0]) || {};
        const tc = delivery.tracking_code_created_at;
        const after = Number(order.delivered_status) > 5 || (tc && Date.parse(String(tc).replace(' ', 'T')) < Date.now() - 86400000);
        const w = this.returnWindow(order);
        return !!after && !!w && (w === 'open' ? (channel.isReturn || channel.isChange) : channel.isReturn);
    },
    // an item can be changed to another variant of the same product
    hasOtherVariants(order) {
        return (order.orderItems || []).some((it) => Number(it.quantity) > 0
            && (it.other_variants || []).some((v) => !v.is_original && v.available !== false && (v.stock === undefined || v.stock > 0)));
    },

    /*
     * The actions of an order, in order: [{key, href?, blank?, detail?}]
     * key: pay | address | point | size | items | cancel | track | return | return_change | dispute | refund | invoice | contact
     * channel: {isChange, isReturn} (sale channel settings)
     */
    actions(order, channel) {
        if (!order) return [];
        const s = this.state(order);
        const delivery = (order.orderDeliveries && order.orderDeliveries[0]) || {};
        const lan = window.lan || document.documentElement.lang || 'hu';
        const base = '/' + lan + '/' + ((order.webshop && order.webshop.slug) || '');
        const list = [];
        if (s.pay) list.push({ key: 'pay', href: base + '/payment/' + order.id + '?token=' + order.token });
        if (this.canModify(order)) {
            const point = !!delivery.package_point || (delivery.delivery_method && delivery.delivery_method.type == 2);
            list.push({ key: point ? 'point' : 'address' });
            if (this.hasOtherVariants(order)) list.push({ key: 'size' });
            // remove / add products, the difference paid or refunded
            list.push({ key: 'items' });
            list.push({ key: 'cancel' });
        }
        if (delivery.public_tracking_code && !s.stopped) {
            list.push({ key: 'track', href: '/track/' + delivery.app_id + '/' + delivery.public_tracking_code, blank: true, detail: delivery.public_tracking_code });
        }
        if (!s.returning && this.canReturn(order, channel)) {
            const w = this.returnWindow(order);
            list.push({ key: w === 'open' ? (channel.isChange ? 'return_change' : 'return') : 'dispute' });
        }
        if (this.afterStatus(order).is_refund || [6, 8].includes(Number(order.payment_status))) list.push({ key: 'refund' });
        const invoice = order.orderInvoices && order.orderInvoices[0] && order.orderInvoices[0].invoice;
        if (invoice) list.push({ key: 'invoice', href: '/invoice/' + invoice.id + '/?token=' + invoice.invoice_file, blank: true });
        list.push({ key: 'contact' });
        return list;
    },
};

window.OrderRules = OrderRules;
